class BacneProductGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.bacne-product-gallery__track');
    this.slides = this.querySelectorAll('.bacne-product-gallery__slide');
    this.thumbs = this.closest('media-gallery')?.querySelectorAll('[data-bacne-thumb]') || [];
    this.thumbsContainer = this.closest('media-gallery')?.querySelector('.bacne-product-gallery__thumbs');
    this.currentEl = this.querySelector('[data-bacne-current]');
    this.totalEl = this.querySelector('[data-bacne-total]');
    this.total = parseInt(this.dataset.slideCount, 10) || this.slides.length;

    if (this.totalEl) this.totalEl.textContent = this.total;
    if (!this.track) return;

    this.onScroll = this.updateCounter.bind(this);
    this.onThumbClickHandler = this.onThumbClick.bind(this);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.thumbs.forEach((thumb) => thumb.addEventListener('click', this.onThumbClickHandler));

    if (this.total >= 2) {
      this.setActiveIndex(this.getCenteredIndex(), { scrollThumb: false });
    } else {
      this.setActiveIndex(0, { scrollThumb: false });
    }
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.thumbs.forEach((thumb) => thumb.removeEventListener('click', this.onThumbClickHandler));
  }

  onThumbClick(event) {
    const thumb = event.currentTarget;
    const index = parseInt(thumb.dataset.index, 10);
    const slide = this.slides[index];
    if (!slide || !this.track) return;

    slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    this.setActiveIndex(index);
  }

  setActiveIndex(index, { scrollThumb = true } = {}) {
    this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
    this.thumbs.forEach((thumb, i) => {
      const isActive = i === index;
      thumb.classList.toggle('is-active', isActive);
      if (isActive) {
        thumb.setAttribute('aria-current', 'true');
        if (scrollThumb) this.scrollThumbIntoView(thumb);
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
    if (this.currentEl) this.currentEl.textContent = index + 1;
  }

  scrollThumbIntoView(thumb) {
    const container = this.thumbsContainer;
    if (!container || !thumb) return;

    const thumbLeft = thumb.offsetLeft;
    const thumbRight = thumbLeft + thumb.offsetWidth;
    const visibleLeft = container.scrollLeft;
    const visibleRight = visibleLeft + container.clientWidth;

    if (thumbLeft < visibleLeft) {
      container.scrollTo({ left: thumbLeft, behavior: 'smooth' });
    } else if (thumbRight > visibleRight) {
      container.scrollTo({ left: thumbRight - container.clientWidth, behavior: 'smooth' });
    }
  }

  getCenteredIndex() {
    const trackRect = this.track.getBoundingClientRect();
    const center = trackRect.left + trackRect.width / 2;
    let closest = 0;
    let minDist = Infinity;

    this.slides.forEach((slide, i) => {
      const rect = slide.getBoundingClientRect();
      const slideCenter = rect.left + rect.width / 2;
      const dist = Math.abs(center - slideCenter);
      if (dist < minDist) {
        minDist = dist;
        closest = i;
      }
    });

    return closest;
  }

  updateCounter() {
    this.setActiveIndex(this.getCenteredIndex());
  }
}

class BacneKitPicker extends HTMLElement {
  connectedCallback() {
    this.cards = this.querySelectorAll('[data-bacne-kit]');
    this.form = document.querySelector(this.dataset.form);
    this.variantInput = this.form?.querySelector('.product-variant-id');
    this.productForm = this.form?.closest('product-form');
    this.submitButton = this.productForm?.querySelector('[type="submit"]');
    this.submitButtonText = this.submitButton?.querySelector('span');
    this.submitButtonLabel = this.submitButton?.querySelector('.advertorial-product__cta-label');
    this.submitButtonPrice = this.submitButton?.querySelector('[data-cta-price]');
    this.submitButtonCompare = this.submitButton?.querySelector('[data-cta-compare]');
    this.showCtaPrices = this.dataset.ctaPrices === 'true';
    this.errorWrapper = this.productForm?.querySelector('.product-form__error-message-wrapper');
    this.errorMessage = this.productForm?.querySelector('.product-form__error-message');
    this.defaultButtonLabel = this.dataset.buttonLabel || this.submitButtonText?.textContent?.trim();

    this.cards.forEach((card) => {
      card.addEventListener('click', () => this.selectKit(card));
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        this.updateSubmitState();
      });
    }

    this.updateSubmitState();
  }

  disconnectedCallback() {
    this.cartUpdateUnsubscriber?.();
  }

  getSelectedCard() {
    return this.querySelector('[data-bacne-kit].is-selected');
  }

  updateSubmitState() {
    const selected = this.getSelectedCard();
    if (!selected || !this.submitButton) return;

    const isAvailable = selected.dataset.available !== 'false';
    const buttonLabel = this.defaultButtonLabel;

    this.submitButton.toggleAttribute('disabled', !isAvailable);
    this.submitButton.removeAttribute('aria-disabled');

    if (this.showCtaPrices) {
      if (this.submitButtonLabel) {
        this.submitButtonLabel.textContent = buttonLabel;
      }
      if (this.submitButtonPrice) {
        this.submitButtonPrice.textContent = selected.dataset.priceFormatted || '';
      }
      if (this.submitButtonCompare) {
        if (selected.dataset.hasCompare === 'true' && selected.dataset.compareFormatted) {
          this.submitButtonCompare.textContent = selected.dataset.compareFormatted;
          this.submitButtonCompare.hidden = false;
        } else {
          this.submitButtonCompare.hidden = true;
        }
      }
      return;
    }

    if (this.submitButtonText && buttonLabel) {
      this.submitButtonText.textContent = buttonLabel;
    }
  }

  selectKit(card) {
    const variantId = card.dataset.variantId;
    if (!variantId || !this.variantInput) return;

    this.cards.forEach((c) => c.classList.remove('is-selected'));
    card.classList.add('is-selected');

    this.variantInput.value = variantId;
    this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));

    this.hideError();
    this.updateSubmitState();

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url.toString());
  }

  showError(message) {
    if (!this.errorWrapper || !this.errorMessage) return;
    this.errorMessage.textContent = message;
    this.errorWrapper.removeAttribute('hidden');
  }

  hideError() {
    if (!this.errorWrapper) return;
    this.errorWrapper.setAttribute('hidden', '');
  }
}

class BacneCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-bacne-track]');
    this.slides = this.querySelectorAll('[data-bacne-slide]');
    this.prevBtn = this.querySelector('[data-bacne-prev]');
    this.nextBtn = this.querySelector('[data-bacne-next]');
    this.dots = this.querySelectorAll('[data-bacne-dot]');
    this.isRtl = this.hasAttribute('data-bacne-rtl');
    this.lastIndex = this.slides.length - 1;
    this.currentIndex = this.isRtl ? 0 : this.lastIndex;

    if (!this.track || this.slides.length === 0) return;

    this.onScroll = this.handleScroll.bind(this);
    this.prevBtn?.addEventListener('click', () => this.goTo(this.currentIndex - 1));
    this.nextBtn?.addEventListener('click', () => this.goTo(this.currentIndex + 1));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    requestAnimationFrame(() => this.goTo(this.currentIndex, { instant: true }));
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
  }

  getCenteredIndex() {
    const trackRect = this.track.getBoundingClientRect();
    const center = trackRect.left + trackRect.width / 2;
    let closest = 0;
    let minDist = Infinity;

    this.slides.forEach((slide, i) => {
      const rect = slide.getBoundingClientRect();
      const slideCenter = rect.left + rect.width / 2;
      const dist = Math.abs(center - slideCenter);
      if (dist < minDist) {
        minDist = dist;
        closest = i;
      }
    });

    return closest;
  }

  centerSlide(slide, { instant = false } = {}) {
    const trackRect = this.track.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    const offset =
      slideRect.left + slideRect.width / 2 - (trackRect.left + trackRect.width / 2);

    if (Math.abs(offset) < 1) return;

    this.track.scrollBy({
      left: offset,
      behavior: instant ? 'instant' : 'smooth',
    });
  }

  goTo(index, { instant = false } = {}) {
    const clamped = Math.max(0, Math.min(index, this.lastIndex));
    const slide = this.slides[clamped];
    if (!slide) return;

    this.currentIndex = clamped;
    this.centerSlide(slide, { instant });
    this.updateUI();
  }

  handleScroll() {
    const centered = this.getCenteredIndex();
    if (centered !== this.currentIndex) {
      this.currentIndex = centered;
      this.updateUI();
    }
  }

  updateUI() {
    this.updateDots();
    this.updateArrows();
  }

  updateDots() {
    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === this.currentIndex));
  }

  updateArrows() {
    if (this.prevBtn) this.prevBtn.disabled = this.currentIndex === 0;
    if (this.nextBtn) this.nextBtn.disabled = this.currentIndex === this.lastIndex;
  }
}

function bacneCapture(eventName, properties = {}) {
  if (typeof window.posthog?.capture !== 'function') return;
  window.posthog.capture(eventName, properties);
}

function bacneSectionName(element) {
  if (!element) return 'unknown';
  if (element.dataset?.ctaSection) return element.dataset.ctaSection;
  if (element.dataset?.faqSection) return element.dataset.faqSection;

  const section = element.closest('.shopify-section');
  if (!section?.id) return 'unknown';

  return section.id
    .replace(/^shopify-section-/, '')
    .replace(/^template--[\w-]+__/, '');
}

class BacneFaq extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('.bacne-faq__item').forEach((details) => {
      const summary = details.querySelector('summary');
      if (!summary) return;

      if (details.hasAttribute('open')) {
        details.classList.add('is-expanded', 'is-visible');
      }

      summary.addEventListener('click', (event) => {
        event.preventDefault();
        const expanding = !details.classList.contains('is-expanded');
        const question =
          [...summary.childNodes]
            .filter((node) => node.nodeType === Node.TEXT_NODE)
            .map((node) => node.textContent.trim())
            .filter(Boolean)
            .join(' ') ||
          summary.textContent?.trim() ||
          '';

        bacneCapture('faq_clicked', {
          question,
          section: bacneSectionName(this),
          action: expanding ? 'expand' : 'collapse',
        });

        if (expanding) {
          this.expand(details, summary);
        } else {
          this.collapse(details, summary);
        }
      });
    });
  }

  expand(details, summary) {
    details.setAttribute('open', '');
    details.classList.add('is-expanded');
    summary.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => details.classList.add('is-visible'));
  }

  collapse(details, summary) {
    details.classList.remove('is-visible');

    const finishCollapse = () => {
      details.classList.remove('is-expanded');
      details.removeAttribute('open');
      summary.setAttribute('aria-expanded', 'false');
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      finishCollapse();
      return;
    }

    const answer = details.querySelector('.bacne-faq__answer');
    if (!answer) return;

    const onTransitionEnd = (event) => {
      if (event.target !== answer || event.propertyName !== 'opacity') return;
      answer.removeEventListener('transitionend', onTransitionEnd);
      finishCollapse();
    };

    answer.addEventListener('transitionend', onTransitionEnd);
  }
}

class BacneScienceTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = this.querySelectorAll('[data-bacne-science-tab]');
    this.panels = this.querySelectorAll('[data-bacne-science-panel]');

    if (this.tabs.length === 0 || this.panels.length === 0) return;

    this.onTabClick = this.handleTabClick.bind(this);
    this.tabs.forEach((tab) => tab.addEventListener('click', this.onTabClick));
    this.syncPanelVideos(this.panels[0]);
  }

  disconnectedCallback() {
    this.tabs?.forEach((tab) => tab.removeEventListener('click', this.onTabClick));
  }

  syncPanelVideos(activePanel) {
    this.panels.forEach((panel) => {
      const video = panel.querySelector('.bacne-science__video');
      if (!video) return;

      if (panel === activePanel) {
        video.currentTime = 0;
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }

  handleTabClick(event) {
    const tab = event.currentTarget;
    const index = Array.from(this.tabs).indexOf(tab);
    if (index === -1) return;

    this.tabs.forEach((item, i) => {
      const isActive = i === index;
      item.classList.toggle('is-active', isActive);
      item.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    let activePanel = null;

    this.panels.forEach((panel, i) => {
      const isActive = i === index;
      panel.classList.toggle('is-active', isActive);
      panel.toggleAttribute('hidden', !isActive);
      if (isActive) activePanel = panel;
    });

    this.syncPanelVideos(activePanel);
  }
}

class BacneStickyCta extends HTMLElement {
  connectedCallback() {
    this.scrollTarget = document.querySelector(this.dataset.scrollTarget || '#bacne-purchase');
    this.scrollButton = this.querySelector('[data-sticky-scroll]');
    this.heroTarget =
      document.querySelector('product-info.bacne-hero') ||
      document.querySelector('.bacne-hero:not(.bacne-purchase)');
    this.offerTarget = document.querySelector('#bacne-purchase') || document.querySelector('.bacne-purchase');
    this.ctaTargets = [...document.querySelectorAll('[data-bacne-cta]')].filter(
      (el) => !this.contains(el)
    );

    if (!this.scrollButton || this.ctaTargets.length === 0) return;

    this.heroVisible = Boolean(this.heroTarget);
    this.offerVisible = false;
    this.ctaVisibility = new Map();
    this.anyCtaVisible = false;

    this.sectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.heroTarget) {
            this.heroVisible = entry.isIntersecting;
          }
          if (entry.target === this.offerTarget) {
            this.offerVisible = entry.isIntersecting;
          }
        });
        this.updateVisibility();
      },
      { threshold: 0, rootMargin: '0px 0px -25% 0px' }
    );

    if (this.heroTarget) this.sectionObserver.observe(this.heroTarget);
    if (this.offerTarget) this.sectionObserver.observe(this.offerTarget);

    this.ctaObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.ctaVisibility.set(entry.target, entry.isIntersecting);
        });
        this.anyCtaVisible = [...this.ctaVisibility.values()].some(Boolean);
        this.updateVisibility();
      },
      { threshold: 0, rootMargin: '0px 0px -8px 0px' }
    );

    this.ctaTargets.forEach((target) => {
      this.ctaVisibility.set(target, false);
      this.ctaObserver.observe(target);
    });

    this.onScrollClick = this.handleScrollClick.bind(this);
    this.scrollButton.addEventListener('click', this.onScrollClick);
  }

  disconnectedCallback() {
    this.sectionObserver?.disconnect();
    this.ctaObserver?.disconnect();
    this.scrollButton?.removeEventListener('click', this.onScrollClick);
  }

  handleScrollClick() {
    bacneCapture('cta_clicked', {
      section: 'sticky_footer',
      label: this.scrollButton.textContent?.trim() || '',
      cta_type: 'sticky',
    });

    this.scrollTarget?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  updateVisibility() {
    const shouldShow = !this.heroVisible && !this.offerVisible && !this.anyCtaVisible;
    this.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    this.classList.toggle('is-visible', shouldShow);
  }
}

function setupBacneLoopVideo(video) {
  if (!(video instanceof HTMLVideoElement) || video.dataset.bacneLoopReady === 'true') return;

  video.dataset.bacneLoopReady = 'true';
  video.controls = false;
  video.removeAttribute('controls');
  video.muted = true;
  video.defaultMuted = true;
  video.playsInline = true;
  video.loop = true;
  video.setAttribute('muted', '');
  video.setAttribute('playsinline', '');
  video.setAttribute('webkit-playsinline', '');
  video.setAttribute('disablepictureinpicture', '');
  video.disablePictureInPicture = true;
  if ('disableRemotePlayback' in video) video.disableRemotePlayback = true;

  const playSilent = () => {
    video.controls = false;
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  };

  const restart = () => {
    try {
      video.currentTime = 0.001;
    } catch (error) {
      // iOS can throw if the video is not ready yet.
    }
    playSilent();
  };

  video.addEventListener('ended', restart);
  video.addEventListener('pause', () => {
    if (document.hidden) return;
    if (video.ended || (video.duration && video.currentTime >= video.duration - 0.25)) {
      restart();
    }
  });

  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) playSilent();
        });
      },
      { threshold: 0.2 }
    );
    observer.observe(video);
  } else {
    playSilent();
  }
}

function initBacneLoopVideos(root = document) {
  root.querySelectorAll('.bacne-triple__video, .bacne-routine__media-video').forEach(setupBacneLoopVideo);
}

customElements.define('bacne-product-gallery', BacneProductGallery);
customElements.define('bacne-kit-picker', BacneKitPicker);
customElements.define('bacne-carousel', BacneCarousel);
customElements.define('bacne-faq', BacneFaq);
customElements.define('bacne-science-tabs', BacneScienceTabs);
customElements.define('bacne-sticky-cta', BacneStickyCta);

document.addEventListener('shopify:section:load', (event) => {
  initBacneLoopVideos(event.target);
});

(() => {
  initBacneLoopVideos();

  document.addEventListener('click', (event) => {
    const studyToggle = event.target.closest('[data-bacne-study-toggle]');
    if (studyToggle) {
      const study = studyToggle.closest('.bacne-study');
      if (study) {
        const open = !study.classList.contains('is-open');
        study.classList.toggle('is-open', open);
        studyToggle.setAttribute('aria-expanded', String(open));
      }
    }

    const cta = event.target.closest('[data-bacne-cta]');
    if (cta) {
      bacneCapture('cta_clicked', {
        section: bacneSectionName(cta),
        label: cta.dataset.ctaLabel || cta.textContent?.trim() || '',
        cta_type: cta.dataset.ctaType || (cta.tagName === 'BUTTON' ? 'submit' : 'link'),
      });
    }
  });

  if (!document.querySelector('.bacne-section')) return;

  const scrollBehavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

  document.documentElement.style.scrollBehavior = scrollBehavior;

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href^="#"]');
    if (!link) return;

    const hash = link.getAttribute('href');
    if (!hash || hash === '#') return;

    const target = document.querySelector(hash);
    if (!target) return;

    event.preventDefault();
    target.scrollIntoView({ behavior: scrollBehavior, block: 'start' });

    if (history.replaceState) {
      history.replaceState(null, '', hash);
    }
  });
})();
